/**
 * NEOPINE showcase — 读数上浮驱动（2026-10-20）
 *
 * 用途
 * ----
 * 给 `sections/np-metrics.liquid` 的指标项做「依次上浮」的入场：
 * 数字先到位、标签后跟上，一件一件来。这是苹果产品页讲参数的标准做法。
 *
 * 为什么单独一个文件、而不写进 neopine-motion.js
 * ---------------------------------------------
 * neopine-motion.js 管的是**区块级**入场（按行、按组、带 stagger 预算），
 * 它的 BLOCK_SELECTOR 不认识 .np-metrics__item 这种「区块内部的读数」。
 * 硬塞进去会动到那条已经签收过的编排逻辑。这里独立成篇，互不干扰。
 *
 * 🔴 安全设计（必须保留）
 * ---------------------
 * 隐藏态写在 CSS 里，但只在 `html.np-showcase` 存在时生效；这个类由本文件
 * 在确认一切就绪后才加。所以：
 *   · 脚本没加载 / 解析失败 / 被 CSP 拦 → 类不存在 → 元素完全可见（静态版式）
 *   · 用户开了「减少动效」→ 不加类（CSS 里另有一道兜底）
 *   · 已经在首屏内的 → 直接标 shown，不播动画
 *
 * 🔴 兜底不能用「无条件定时器」（10-20 踩过）
 * -----------------------------------------
 * 第一版写的是「3 秒后把所有项标为 shown」。实测立刻暴露问题：指标带在
 * 首屏之下，用户通常 3 秒后才滚到 ⇒ 定时器早已把动画全部杀掉，
 * 结果「有入场效果」变成「永远没有入场效果」，而且不易察觉。
 *
 * 正确做法是**只验证 IntersectionObserver 本身是否可用**：往视口里放一个
 * 1px 的哨兵元素，它必须立刻触发回调。
 *   · 哨兵触发了 → IO 正常 ⇒ 什么都不做，让动画按滚动自然播
 *   · 哨兵没触发 → IO 是坏的 ⇒ 撤掉闸门类并全部显示（宁可没有动画，
 *     也绝不留白 —— 这条优先级最高）
 *
 * @version 1.1.0
 */

(function () {
  'use strict';

  /** 加在 <html> 上的闸门类，CSS 的隐藏态以它为条件。 */
  var GATE = 'np-showcase';
  /** 打在指标项上，表示已经播过入场。 */
  var SHOWN = 'data-np-shown';
  /** 进入判定：元素顶部越过视口底部这条线就算进来。 */
  var ROOT_MARGIN = '0px 0px -12% 0px';
  /** 哨兵等多久算「IO 坏了」。 */
  var PROBE_TIMEOUT_MS = 900;

  var root = document.documentElement;

  // ---- 前置条件不满足就直接退出，页面保持静态可见 ----
  if (!('IntersectionObserver' in window)) return;
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var items = document.querySelectorAll('[data-np-m-item]');
  if (!items.length) return;

  /** 撤掉闸门并全部显示 —— 用于「宁可没有动画也不留白」。 */
  function revealAll() {
    root.classList.remove(GATE);
    for (var i = 0; i < items.length; i++) items[i].setAttribute(SHOWN, '');
  }

  // ---- 先把已经在首屏内的项标记好（它们不需要动画，避免闪一下） ----
  var pending = [];
  for (var i = 0; i < items.length; i++) {
    var rect = items[i].getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      items[i].setAttribute(SHOWN, '');
    } else {
      pending.push(items[i]);
    }
  }

  // 全都在首屏内：没什么可观察的，压根不必加闸门。
  if (!pending.length) return;

  // ---- 这里才允许隐藏 ----
  root.classList.add(GATE);

  var observer = new IntersectionObserver(
    function (entries) {
      for (var k = 0; k < entries.length; k++) {
        if (!entries[k].isIntersecting) continue;
        // 错峰交给 CSS 的 --np-m-delay（每项 90ms），这里只决定「何时开始」。
        entries[k].target.setAttribute(SHOWN, '');
        observer.unobserve(entries[k].target);
      }
    },
    { rootMargin: ROOT_MARGIN, threshold: 0.05 }
  );

  for (var j = 0; j < pending.length; j++) observer.observe(pending[j]);

  // ---- 哨兵：只用来判断 IO 到底会不会回调 ----
  var ioAlive = false;
  var sentinel = document.createElement('div');
  sentinel.setAttribute('aria-hidden', 'true');
  sentinel.style.cssText =
    'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;pointer-events:none;';
  document.body.appendChild(sentinel);

  var probe = new IntersectionObserver(function (entries) {
    if (!entries.length || !entries[0].isIntersecting) return;
    ioAlive = true;
    probe.disconnect();
    if (sentinel.parentNode) sentinel.parentNode.removeChild(sentinel);
  });
  probe.observe(sentinel);

  window.setTimeout(function () {
    if (ioAlive) return; // IO 正常 ⇒ 不动，让动画自然播
    if (probe) probe.disconnect();
    if (sentinel.parentNode) sentinel.parentNode.removeChild(sentinel);
    revealAll();
  }, PROBE_TIMEOUT_MS);

  // Shopify 分区渲染 / 主题编辑器换块后会插入新节点，重新接管一次。
  document.addEventListener('shopify:section:load', function () {
    var fresh = document.querySelectorAll('[data-np-m-item]:not([' + SHOWN + '])');
    for (var m = 0; m < fresh.length; m++) observer.observe(fresh[m]);
  });
})();
